import { useMemo, useState, type ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import { useUserSignInQuery } from "@/v2-backoffice-client/user/signIn/user.signIn.query"
import SignInTable from "@/v2-backoffice-client/signIn/SignInTable"
import SharingRiskCard from "@/v2-backoffice-client/signIn/sharingRisk/SharingRiskCard"
import {
  activityMatchesFocused,
  type FocusedSignal,
} from "@/v2-backoffice-client/signIn/sharingRisk/sharingRisk.utils"
import SharingFlagCard from "@/v2-backoffice-client/signIn/sharingRisk/SharingFlagCard"
import OutsideEeaBadge from "@/v2-backoffice-client/signIn/outsideEea/OutsideEeaBadge"

export type UserSignInProps = ComponentProps<"div"> & {
  userId: number
}

function UserSignIn(props: UserSignInProps) {
  const { userId, className, ...divProps } = props

  const { data, loading, error, refetch } = useUserSignInQuery({
    variables: { input: { userId } },
  })

  const activities = data?.getSignInActivitiesByUser?.Activities ?? []
  // Per-user rollup (one row for this user): the authoritative sharing-risk
  // score, computed server-side from the durable event log so it matches the
  // Sign-ins Users table. It also carries the cross-border-access signal (which
  // non-EEA countries, and how many sign-ins). Prefer it over the per-activity
  // detail, which can be empty/partial when the live MS Graph view returned few
  // rows for the window.
  const rollup = data?.getSignInActivitiesByUser?.SharingRisks?.[0] ?? null
  const userRisk = rollup?.Detail ?? activities[0]?.SharingRisk ?? null

  // Clicking a value in the risk card (an IP, a city, a country) narrows the
  // list below it to the sign-ins behind that value, which is the operator's
  // next move after reading the card. Filtered client-side: the panel already
  // holds every activity in the window.
  const [focused, setFocused] = useState<FocusedSignal | null>(null)
  const shownActivities = useMemo(() => {
    if (!focused) return activities
    return activities.filter((activity) =>
      activityMatchesFocused(activity, focused),
    )
  }, [activities, focused])

  return (
    <div
      className={cn("user-sign-in flex flex-col gap-3", className)}
      {...divProps}
    >
      {!loading && userRisk && (
        <SharingRiskCard
          risk={userRisk}
          className="mt-1"
          focusedValue={focused?.value ?? null}
          onFocusValue={(value, signalKey) =>
            setFocused((current) =>
              current?.value === value ? null : { key: signalKey, value },
            )
          }
        />
      )}

      {focused && (
        <div className="flex items-center gap-2 border rounded-md px-3 py-2 text-sm">
          <span className="text-muted-foreground">
            Showing {shownActivities.length} of {activities.length} sign-ins
            matching
          </span>
          <span className="font-medium">{focused.value}</span>
          <button
            type="button"
            className="ml-auto text-xs underline"
            onClick={() => setFocused(null)}
            data-feature-code="UI_BackofficeSignIns_UserPanel_ClearSignalFilter"
          >
            Clear
          </button>
        </div>
      )}

      {!loading && rollup?.IsOutsideEea && (
        <div className="flex items-center gap-2 border rounded-md px-3 py-2 text-sm">
          <OutsideEeaBadge countries={rollup.OutsideEeaCountries} />
          <span className="text-muted-foreground">
            {rollup.OutsideEeaSignInCount} sign-in
            {rollup.OutsideEeaSignInCount === 1 ? "" : "s"} from outside the
            EU/EEA in this window
          </span>
        </div>
      )}

      <SharingFlagCard userId={userId} />

      <SignInTable
        activities={shownActivities}
        isLoading={loading}
        error={error}
        showUser={false}
        tableId="user-sign-in-table"
        exportFileName="user-sign-ins"
        onRefresh={refetch}
      />
    </div>
  )
}

export default UserSignIn
